class PrivateSaleModal extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.overlay = this.els.overlay.element
    this.inputEl = this.els.input.element
    this.submitEl = this.els.submit.element
    this.errorEl = this.els.error.element

    this.saleType = this.dataset.saleType
    this.frozenLinks = []

    this.showSuccess = this.showSuccess.bind(this)
    this.unlockPage = this.unlockPage.bind(this)
    this.checkForm = this.checkForm.bind(this)
    this.clearError = this.clearError.bind(this)
    this.grantAccess = this.grantAccess.bind(this)
    this.handleSubmitButton = this.handleSubmitButton.bind(this)
  }

  static get requiredElements () {
    return ['overlay', 'challenge', 'success', 'error', 'input']
  }

  setupListeners () {
    super.setupListeners()
    this.listeners.add(this.els.form.element, 'submit', this.checkForm)
    this.listeners.add(this.inputEl, 'input', this.clearError)
    this.listeners.add(this.inputEl, 'input', this.handleSubmitButton)
    window.addEventListener('unlockPrivateSale', this.showSuccess)

    this.handleSubmitButton()
  }

  connectedCallback () {
    super.connectedCallback()
    this.validateAccess()

    if (this.type === 'page') {
      this.focusTrap = this.trapFocus()

      const cloak = this.els.collectionCloak.element

      if (cloak) {
        cloak.parentNode.removeChild(cloak)
      }
    }

    if (this.type === 'banner') {
      this.freezeLinks()
    }
  }

  freezeLinks () {
    try {
      this.frozenLinks = Array.from(
        this.parentNode.querySelectorAll('a[href]'),
      ).filter(el => !el.closest(this.tagName))

      this.frozenLinks.forEach(el => {
        el.setAttribute('inert', true)
      })
    } catch (err) {
      console.error('Error freezing links')
      this.frozenLinks = []
    }
  }

  thawLinks () {
    if (this.type === 'page') {
      return
    }

    try {
      this.frozenLinks.forEach(el => {
        el.removeAttribute('inert')
      })
    } catch (err) {
      console.error('Error thawing links')
    }
  }

  lockScroll () {
    this.savedScrollY = window.scrollY
    document.body.classList.add('util-SiteOverflowed')
    document.body.style.position = 'fixed'
    document.body.style.top = `-${this.savedScrollY}px`
    document.body.style.width = '100%'
  }

  unlockScroll () {
    document.body.classList.remove('util-SiteOverflowed')
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    window.scrollTo(0, this.savedScrollY || 0)
  }

  releaseFocus () {
    if (this.type === 'banner') {
      return
    }

    this.unlockScroll()

    try {
      this.elementsToLock.forEach(elem => {
        elem.removeAttribute('inert')
        elem.removeAttribute('aria-hidden')
      })
    } catch (err) {
      console.error('Error releasing focus')
    }
  }

  trapFocus () {
    this.lockScroll()
    this.inputEl?.focus()

    this.elementsToLock.forEach(elem => {
      elem.setAttribute('inert', true)
      elem.setAttribute('aria-hidden', true)
    })
  }

  handleSubmitButton () {
    const input = this.inputEl?.value
    if (!input) {
      this.submitEl?.setAttribute('disabled', true)
    } else {
      this.submitEl?.removeAttribute('disabled')
    }
  }

  async validateAccess () {
    let accessGranted = this.urlParamsAccess()

    if (this.saleType === 'email' && this.cookieAccess()) {
      accessGranted = true
    }

    if (accessGranted) {
      this.showSuccess()
    }
  }

  async newsletterSubscribe (emailAddress) {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: emailAddress,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.listId,
            },
          },
        },
      },
    }

    const options = {
      method: 'POST',
      headers: {
        revision: '2023-12-15',
        'content-type': 'application/json',
      },
      body: JSON.stringify(payload),
    }

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoApiKey}`,
        options,
      )

      if (response.status === 202) {
        window.BAO.Utils.setCookie(`isRegistered`, true, 31)
        this.showSuccess()
      } else {
        const error = response.error || this.genericError
        this.showError(error)
      }
    } catch (error) {
      console.error('An error occurred during the fetch operation:', error)
    } finally {
      this.removeAttribute('aria-busy')
    }
  }

  checkForm (evt) {
    evt.preventDefault()

    if (this.saleType === 'email') {
      this.handleEmailPromo(evt)
    } else if (this.saleType === 'passcode') {
      this.handleCodePromo(evt)
    }
  }

  handleEmailPromo (evt) {
    if (!this.inputEl.validity.valid) {
      this.showError(this.genericError)
      return
    }

    this.setAttribute('aria-busy', 'true')

    this.newsletterSubscribe(this.inputEl.value)
  }

  handleCodePromo (evt) {
    const passcode = window.atob(this.dataset.pc)
    if (!this.inputEl || this.inputEl.value !== passcode) {
      this.showError(this.genericError)
      return
    }

    this.setAttribute('aria-busy', 'true')
    this.showSuccess()
    this.setAttribute('aria-busy', 'false')
  }

  grantAccess () {
    this.unlockPage()
    this.setCartAttribute()
  }

  hide () {
    this.overlay.setAttribute('aria-hidden', true)
  }

  // Unlock all other modals at the same time
  removeAll () {
    Array.from(document.querySelectorAll(this.tagName))
      .filter(modal => modal !== this)
      .forEach(modal => {
        modal.hide()
        modal.thawLinks()
      })
  }

  removeBodyClass () {
    document.body.classList.remove(this.bodyClass)
  }

  unlockPage () {
    this.hide()

    document.dispatchEvent(new CustomEvent('clear-transparent-header'))
    this.releaseFocus()

    this.thawLinks()
    this.removeAll()
    this.removeBodyClass()
  }

  showError (message) {
    this.errorEl.innerText = message
    this.errorEl?.removeAttribute('aria-hidden')
  }

  clearError () {
    this.errorEl.innerText = ''
    this.errorEl.setAttribute('aria-hidden', true)
  }

  showSuccess () {
    this.els.success.element.removeAttribute('aria-hidden')
    this.els.challenge.element.setAttribute('aria-hidden', true)
    this.unlockScroll()
    this.overlay.setAttribute('data-fade', true)
    window.setTimeout(this.grantAccess, 2000)
  }

  async setCartAttribute () {
    const res = await fetch(`${window.routes.cart_url}.js`)
    const { attributes } = await res.json()

    const newAttributes = {
      ...attributes,
      [this.cartAttributeKey]: true,
    }

    try {
      fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: newAttributes }),
      })
    } catch (err) {
      console.error(err.message)
    }
  }

  urlParamsAccess () {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const { key, value } = this.config.urlPasscode

      if (key && value) {
        return value === urlParams.get(key)
      }

      return false
    } catch (err) {
      console.error(err)

      return false
    }
  }

  cookieAccess () {
    try {
      return window.BAO.Utils.getCookie('isRegistered') === 'true'
    } catch (err) {
      console.error(err)
      return false
    }
  }

  get config () {
    const rawData = window.atob(this.dataset.config)
    const config = JSON.parse(rawData)

    return config
  }

  get klaviyoApiKey () {
    return window.theme.klaviyo.apiKey
  }

  get listId () {
    return this.config.listId
  }

  get type () {
    return this.config.type
  }

  get cartAttributeKey () {
    return window.atob(this.dataset.attributeKey)
  }

  get genericError () {
    return this.dataset.genericError
  }

  get elementsToLock () {
    const selectors = ['#MainContent', '#shopify-section-footer']

    return selectors
      .map(selector => document.querySelector(selector))
      .filter(el => el)
  }

  get bodyClass () {
    return this.config.bodyClass
  }
}

if (!customElements.get('private-sale-modal')) {
  customElements.define('private-sale-modal', PrivateSaleModal)
}
